import { ArrowRight, Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedEssayConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const fallbackTitle = "Laporan Utama: Dinamika Politik Nasional Terkini";
  const fallbackExcerpt = "Di tengah hiruk pikuk ibu kota, perdebatan mengenai arah kebijakan nasional kembali memanas. Dari gedung parlemen hingga pelosok daerah, aspirasi masyarakat menuntut transparansi dan langkah nyata. Laporan mendalam ini mengupas tuntas berbagai isu strategis yang sedang menjadi sorotan publik, memberikan perspektif objektif bagi Anda yang ingin memahami arah bangsa di masa depan.";
  const featuredPost = posts && posts.length > 0 ? posts[0] : null;
  const title = featuredPost?.title || fallbackTitle;
  const excerpt = featuredPost?.excerpt || fallbackExcerpt;
  const link = featuredPost?.link || "#";
  const authorName = featuredPost?.author?.name || "Redaksi ABADI";
  const featuredImage = featuredPost?.featuredImage;
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "14 NOV 2024";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }).toUpperCase();
    } catch {
      return "14 NOV 2024";
    }
  };
  const stripHtml = (html: string) => {
    return html.replace(/<[^>]*>/g, "").trim();
  };
  const renderEditorialTitle = (t: string) => {
    const cleaned = stripHtml(t);
    const words = cleaned.split(" ");
    if (words.length < 4) {
      return <span>{cleaned}</span>;
    }
    const accentIndex = Math.floor(words.length / 2);
    return <>
        {words.map((word, i) => <span key={i}>
            {i === accentIndex ? <em className="font-serif italic">{word}</em> : word}
            {i < words.length - 1 ? " " : ""}
          </span>)}
      </>;
  };
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7 space-y-6">
          <div className="h-3 w-48 bg-muted/40 animate-pulse" />
          <div className="h-16 w-full bg-muted/40 animate-pulse" />
          <div className="h-px w-full bg-border" />
          <div className="h-3 w-72 bg-muted/40 animate-pulse" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-muted/30 animate-pulse" />
            <div className="h-4 w-full bg-muted/30 animate-pulse" />
            <div className="h-4 w-3/4 bg-muted/30 animate-pulse" />
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="aspect-[4/5] w-full bg-muted/40 animate-pulse" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-sm font-mono uppercase tracking-widest text-muted-foreground">
        Gagal memuat berita utama.
      </div>;
  }
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
      {}
      <div className="lg:col-span-7 flex flex-col relative">
        {}
        <div className="font-mono text-[11px] md:text-xs uppercase tracking-[0.16em] text-foreground mb-8" style={{
        color: "var(--primary)"
      }}>
          § BERITA UTAMA — EDISI NASIONAL
        </div>

        {}
        <h2 className="font-serif font-normal text-foreground leading-[1.05]" style={{
        fontSize: "clamp(2.5rem, 4.4vw, 3.75rem)",
        letterSpacing: "-0.022em"
      }}>
          {renderEditorialTitle(title)}
        </h2>

        {}
        <div className="flex items-center mt-8 mb-6">
          <div className="h-px flex-1 bg-border" />
          <div className="h-1.5 w-1.5 rounded-lg ml-0" style={{
          backgroundColor: "var(--primary)"
        }} />
        </div>

        {}
        <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-8">
          {formatDate(featuredPost?.date)} · OLEH {authorName.toUpperCase()} · NASIONAL · 5 MENIT BACA
        </div>

        {}
        <div className="hidden lg:block absolute left-0 top-[2px] -translate-x-full pr-6 w-44 xl:w-52">
          <p className="font-serif italic text-[11px] xl:text-xs text-muted-foreground leading-relaxed text-right">
            laporan eksklusif tim redaksi abadi.news
          </p>
        </div>

        {}
        <div className="font-serif italic text-muted-foreground space-y-5" style={{
        fontSize: "17px",
        lineHeight: 1.65
      }}>
          <p>{stripHtml(excerpt).slice(0, 280)}{stripHtml(excerpt).length > 280 ? "…" : ""}</p>
        </div>

        {}
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
          <Link to={link}>
            <Button className="font-mono uppercase tracking-[0.12em] text-xs rounded-sm px-7 py-3.5 h-auto bg-primary text-primary-foreground hover:bg-primary/90">
              Baca Selengkapnya
            </Button>
          </Link>

          <Link to={link} className="inline-flex items-center gap-2 font-serif italic text-base text-foreground hover:text-primary transition-colors duration-200 border-b border-foreground hover:border-primary pb-0.5 w-fit">
            <Download className="size-4" strokeWidth={1.25} />
            Unduh PDF
            <ArrowRight className="size-4" strokeWidth={1.25} />
          </Link>
        </div>
      </div>

      {}
      <div className="lg:col-span-5 relative">
        {}
        <div aria-hidden="true" className="hidden lg:block absolute -bottom-6 -left-10 font-serif font-normal pointer-events-none select-none leading-none" style={{
        fontSize: "16rem",
        color: "var(--foreground)",
        opacity: 0.07,
        zIndex: 0
      }}>
          01
        </div>

        <div className="relative z-10">
          {}
          <div className="relative">
            {}
            <span aria-hidden="true" className="absolute -top-2 -left-2 w-3.5 h-3.5 border-t border-l border-foreground" />
            <span aria-hidden="true" className="absolute -top-2 -right-2 w-3.5 h-3.5 border-t border-r border-foreground" />
            <span aria-hidden="true" className="absolute -bottom-2 -left-2 w-3.5 h-3.5 border-b border-l border-foreground" />
            <span aria-hidden="true" className="absolute -bottom-2 -right-2 w-3.5 h-3.5 border-b border-r border-foreground" />

            <div className="border border-border overflow-hidden" style={{
            aspectRatio: "4 / 5"
          }}>
              {featuredImage ? <img src={featuredImage} alt={stripHtml(title)} loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1580001/img/berita_utama_nasional.png" alt="Suasana rapat strategis di gedung parlemen nasional" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
            </div>
          </div>

          {}
          <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
            — {authorName.toUpperCase()}, JURNALIS ABADI
          </div>
        </div>
      </div>
    </div>;
}
export default function Breakingnewsticker() {
  const wp_query_params = {
    post_type: "post",
    per_page: 1,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="1913" id="breakingnewsticker" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 border-t border-border">
      <div className="max-w-[1280px] mx-auto">
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 1,
        order: "desc",
        orderby: "date"
      }}>
          <FeaturedEssayConsumer />
        </PostsProvider>
      </div>
    </section>;
}